"use client";

import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { m, AnimatePresence } from "motion/react";
import { X } from "lucide-react";

import { MotionFeatures } from "../../lib/motion-features";
import { cn } from "../../lib/utils";

type SheetContextValue = { open?: boolean };
const SheetContext = React.createContext<SheetContextValue>({});

const Sheet = ({
	open,
	...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) => {
	const contextValue = React.useMemo(() => ({ open }), [open]);

	return (
		<SheetContext.Provider value={contextValue}>
			<SheetPrimitive.Root open={open} {...props} />
		</SheetContext.Provider>
	);
};

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Overlay
		data-interactive
		className={cn(
			"fixed inset-0 z-[5000] bg-overlay/70 ease-smooth-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:duration-panel-out data-[state=open]:fade-in-0 data-[state=open]:duration-panel-in",
			className,
		)}
		{...props}
		ref={ref}
	/>
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
	"fixed z-[5000] gap-4 bg-popover p-6 font-sans shadow-lg transition ease-smooth-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-panel-out data-[state=open]:duration-panel-in dark:bg-neutral-950",
	{
		variants: {
			side: {
				top: "inset-x-0 top-0 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
				bottom:
					"inset-x-0 bottom-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
				left: "inset-y-0 left-0 h-full w-3/4 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
				right:
					"inset-y-0 right-0 h-full w-3/4 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
			},
		},
		defaultVariants: {
			side: "right",
		},
	},
);

interface SheetContentProps
	extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
		VariantProps<typeof sheetVariants> {
	modal?: boolean;
	isOpen?: boolean;
	displayCloseButton?: boolean;
	hideOverlay?: boolean;
	dispatchScrollOnAnimationEnd?: boolean;
}

const SheetContent = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Content>,
	SheetContentProps
>(
	(
		{
			side = "right",
			className,
			children,
			modal = true,
			isOpen = false,
			displayCloseButton = true,
			hideOverlay = false,
			dispatchScrollOnAnimationEnd = true,
			onAnimationEnd,
			...props
		},
		ref,
	) => {
		const { open: sheetOpen } = React.useContext(SheetContext);
		const visible = sheetOpen ?? isOpen;

		return (
			<SheetPortal>
				{!hideOverlay &&
					(modal ? (
						<SheetOverlay />
					) : (
						<MotionFeatures>
							<AnimatePresence>
								{visible && (
									<m.span
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										// `exit.transition` replaces the prop below rather than merging with
										// it, so the easing has to be repeated here to survive the exit.
										exit={{
											opacity: 0,
											transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
										}}
										transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
										className="fixed inset-0 z-[5000] bg-overlay/70"
										data-interactive
									/>
								)}
							</AnimatePresence>
						</MotionFeatures>
					))}
				<SheetPrimitive.Content
					ref={ref}
					className={cn(sheetVariants({ side }), className)}
					data-slot="sheet-content"
					onAnimationEnd={(event) => {
						onAnimationEnd?.(event);
						if (
							dispatchScrollOnAnimationEnd &&
							event.target === event.currentTarget
						) {
							event.currentTarget.dispatchEvent(new Event("scroll"));
						}
					}}
					{...props}
				>
					{children}
					{displayCloseButton && (
						<SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-neutral-100 dark:ring-offset-neutral-950 dark:focus:ring-neutral-300 dark:data-[state=open]:bg-neutral-800">
							<X className="h-4 w-4" />
							<span className="sr-only">Close</span>
						</SheetPrimitive.Close>
					)}
				</SheetPrimitive.Content>
			</SheetPortal>
		);
	},
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col space-y-2 text-center sm:text-left",
			className,
		)}
		{...props}
	/>
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
			className,
		)}
		{...props}
	/>
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Title
		ref={ref}
		className={cn(
			"text-lg font-semibold text-neutral-950 dark:text-neutral-50",
			className,
		)}
		{...props}
	/>
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Description
		ref={ref}
		className={cn("text-sm text-neutral-500 dark:text-neutral-400", className)}
		{...props}
	/>
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
	Sheet,
	SheetPortal,
	SheetOverlay,
	SheetTrigger,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetFooter,
	SheetTitle,
	SheetDescription,
	sheetVariants,
	type SheetContentProps,
};
